import React, { useEffect, useMemo, useState } from "react";
import SelectBoxComponent from "./SelectBoxComponent";
import ReverseCountryPicker from "./ReverseCountryPicker";
import VisaRequirementsTable from "./VisaRequirementsTable";
import LeafletMap from "./LeafLetMap";
import { COUNTRY_TO_ISO3, ISO3_TO_COUNTRY } from "./constants/countries";

// Selection is shareable via the query string:
//   outbound (default): ?p=USA,ZAF
//   reverse lookup:      ?mode=reverse&c=USA
const stateFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("mode") === "reverse") {
    const name = ISO3_TO_COUNTRY[params.get("c")];
    return { mode: "reverse", reverseCountry: name || null };
  }
  const codes = params.get("p");
  if (!codes) return { mode: "outbound", selectedCountries: null };
  const names = codes
    .split(",")
    .map((code) => ISO3_TO_COUNTRY[code])
    .filter(Boolean);
  return {
    mode: "outbound",
    selectedCountries: names.length > 0 ? names : null,
  };
};

const ParentComponent = () => {
  const [initialUrlState] = useState(stateFromUrl);
  const [mode, setMode] = useState(initialUrlState.mode);
  const [selectedCountries, setSelectedCountries] = useState(
    () => initialUrlState.selectedCountries || ["Afghanistan"]
  );
  const [reverseCountry, setReverseCountry] = useState(
    () => initialUrlState.reverseCountry || "United States"
  );
  const [combinedVisaReqs, setCombinedVisaReqs] = useState({});
  const [dataLastUpdated, setDataLastUpdated] = useState(null);

  const isReverse = mode === "reverse";

  useEffect(() => {
    if (isReverse) {
      window.history.replaceState(
        null,
        "",
        `?mode=reverse&c=${COUNTRY_TO_ISO3[reverseCountry]}`
      );
      return;
    }
    const codes = selectedCountries
      .map((country) => COUNTRY_TO_ISO3[country])
      .join(",");
    window.history.replaceState(null, "", `?p=${codes}`);
  }, [isReverse, selectedCountries, reverseCountry]);

  useEffect(() => {
    fetch("data-meta.json")
      .then((response) => response.json())
      .then((meta) => setDataLastUpdated(meta.lastUpdated))
      .catch((error) =>
        console.error("Error fetching data metadata:", error)
      );
  }, []);

  const handleSelectChange = (index, value, remove) => {
    if (remove) {
      setSelectedCountries((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    setSelectedCountries((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // Stable array identity so LeafletMap's effect (which depends on this
  // array by reference) doesn't re-fire every render in reverse mode.
  const mapSelectedCountries = useMemo(
    () => (isReverse ? [reverseCountry] : selectedCountries),
    [isReverse, reverseCountry, selectedCountries]
  );

  const mrz = isReverse
    ? `P<${COUNTRY_TO_ISO3[reverseCountry]}<<WHO<CAN<VISIT<US<FREELY`
        .padEnd(64, "<")
        .slice(0, 64)
    : `P<${selectedCountries
        .map((country) => COUNTRY_TO_ISO3[country])
        .join("<")}<<WHERE<CAN<WE<ALL<GO`
        .padEnd(64, "<")
        .slice(0, 64);

  return (
    <div className="max-w-[1100px] mx-auto px-5 py-10">
      <header className="mb-7">
        <p className="font-mono text-xs font-medium tracking-[0.22em] uppercase text-navy mb-2">
          Passport Pals
        </p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight max-w-[24ch] mb-2">
          {isReverse
            ? "Who does this country let in?"
            : "Where can we all go together?"}
        </h1>
        <p className="text-ink-secondary max-w-[58ch]">
          {isReverse
            ? "Pick a country below to see which passports it waves in without a visa — blue means visa-free entry, red means a visa is required first."
            : "Pick everyone's passports below. Each country is coloured by the toughest visa requirement anyone in your group would face — blue means you can all just go, red means someone needs a visa. Try out the shuffle!"}
        </p>
        <p className="mrz mt-6" aria-hidden="true">
          {mrz}
        </p>
      </header>

      <div
        className="inline-flex rounded-md border border-hairline bg-white p-1"
        role="tablist"
        aria-label="Lookup direction"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!isReverse}
          onClick={() => setMode("outbound")}
          className={`px-3.5 py-1.5 rounded-[5px] font-mono text-[0.78rem] transition-colors ${
            !isReverse
              ? "bg-navy text-white"
              : "text-ink-muted hover:text-navy"
          }`}
        >
          Where can we go?
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isReverse}
          onClick={() => setMode("reverse")}
          className={`px-3.5 py-1.5 rounded-[5px] font-mono text-[0.78rem] transition-colors ${
            isReverse
              ? "bg-navy text-white"
              : "text-ink-muted hover:text-navy"
          }`}
        >
          Who can visit us?
        </button>
      </div>

      {isReverse ? (
        <ReverseCountryPicker
          selectedCountry={reverseCountry}
          onChange={setReverseCountry}
        />
      ) : (
        <SelectBoxComponent
          selectedCountries={selectedCountries}
          onSelectChange={handleSelectChange}
        />
      )}

      <LeafletMap
        mode={isReverse ? "reverse" : "outbound"}
        selectedCountries={mapSelectedCountries}
        setCombinedVisaReqs={setCombinedVisaReqs}
      />
      <VisaRequirementsTable
        combinedVisaReqs={combinedVisaReqs}
        homeLabel={isReverse ? "Selected Country" : "Pals Passports"}
      />
      <footer className="mt-6 pt-4 border-t border-hairline text-[0.82rem] text-ink-muted space-y-1">
        <p className="max-w-[72ch]">
          Visa requirement data from{" "}
          <a
            className="text-navy underline"
            href="https://github.com/imorte/passport-index-data"
          >
            imorte/passport-index-data
          </a>{" "}
          (a maintained fork of{" "}
          <a
            className="text-navy underline"
            href="https://github.com/ilyankou/passport-index-dataset"
          >
            ilyankou/passport-index-dataset
          </a>
          , both sourced from{" "}
          <a className="text-navy underline" href="https://www.passportindex.org/">
            Passport Index
          </a>
          ){dataLastUpdated ? `. Data last updated: ${dataLastUpdated}` : ""}.
        </p>
        <p className="max-w-[72ch]">
          Always double-check entry requirements with official sources before
          booking — this map is a planning aid, not travel advice.
        </p>
      </footer>
    </div>
  );
};

export default ParentComponent;
